import { ScrollReveal } from './ScrollReveal';
import { SubtleStars } from './AtmosphericGrain';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function ClipItCaseStudy() {
  return (
    <div className="min-h-screen text-slate-50 relative overflow-x-hidden bg-gradient-to-b from-[#0a1628] via-[#0d1a2d] to-[#091422]">
      {/* Atmospheric grain */}
      <div className="fixed inset-0 pointer-events-none z-[1] opacity-30">
        <SubtleStars count={60} className="opacity-40" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-cyan-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-cyan-200/70 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Portfolio</span>
          </Link>
          <img
            src="/images/portfoliotitle.png"
            alt="Julian Matos"
            className="h-7"
            style={{
              filter: 'invert(85%) sepia(25%) saturate(450%) hue-rotate(330deg) brightness(105%)'
            }}
          />
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 pt-24">
        {/* HERO */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <ScrollReveal>
            <div className="mb-10">
              <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
                <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
                <span>Case Study</span>
              </div>

              <h1 className="text-editorial-hero font-bold text-white mb-6">
                ClipIt
              </h1>

              <p className="text-editorial-title text-[#ffe2bc]/90 max-w-3xl leading-relaxed mb-10">
                Turning the media you already watch into a way to learn.
              </p>

              <p className="text-editorial-body text-slate-200/70 max-w-2xl leading-relaxed">
                ClipIt is a browser extension + web app that turns YouTube and Netflix into language-learning material. You watch content in your target language, ClipIt captures useful vocabulary, and those words return as contextual spaced-repetition practice.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 mb-12">
              <div>
                <div className="text-editorial-meta text-cyan-400/60 uppercase tracking-wider mb-2">Role</div>
                <div className="text-editorial-body text-slate-200">Product Design, UI/UX, Development</div>
              </div>
              <div>
                <div className="text-editorial-meta text-cyan-400/60 uppercase tracking-wider mb-2">Team</div>
                <div className="text-editorial-body text-slate-200">4 people</div>
              </div>
              <div>
                <div className="text-editorial-meta text-cyan-400/60 uppercase tracking-wider mb-2">Timeline</div>
                <div className="text-editorial-body text-slate-200">2 academic terms</div>
              </div>
              <div>
                <div className="text-editorial-meta text-cyan-400/60 uppercase tracking-wider mb-2">Platforms</div>
                <div className="text-editorial-body text-slate-200">Chrome Extension + Web App</div>
              </div>
              <div>
                <div className="text-editorial-meta text-cyan-400/60 uppercase tracking-wider mb-2">Languages</div>
                <div className="text-editorial-body text-slate-200">Korean + Ukrainian</div>
              </div>
              <div>
                <div className="text-editorial-meta text-cyan-400/60 uppercase tracking-wider mb-2">Pilot</div>
                <div className="text-editorial-body text-slate-200">~20 students</div>
              </div>
            </div>
          </ScrollReveal>

          {/* Large product visual */}
          <ScrollReveal delay={0.3}>
            <div className="relative rounded overflow-hidden bg-slate-900/30 border border-cyan-900/20">
              <img
                src="/images/ci2.portfolio.png"
                alt="ClipIt product interface"
                className="w-full h-auto"
              />
            </div>
          </ScrollReveal>
        </section>

        {/* 01 — THE PROBLEM */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>01 — The Problem</span>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-12">
            <ScrollReveal className="col-span-12 lg:col-span-7">
              <h2 className="text-editorial-statement text-white font-semibold mb-6 leading-tight">
                Learning apps were effective or enjoyable. Rarely both.
              </h2>

              <div className="space-y-6 text-editorial-body text-slate-200/70">
                <p>
                  Learners spend time on tools like Duolingo or flashcards but still struggle to understand authentic content. Taking notes during Netflix or YouTube interrupts the experience and turns watching into homework.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="col-span-12 lg:col-span-5">
              <div className="bg-slate-900/30 border border-cyan-900/20 rounded p-8">
                <div className="text-editorial-body text-cyan-300 font-medium mb-4">
                  Design Question
                </div>
                <p className="text-editorial-body text-slate-200/80 italic leading-relaxed">
                  How might we let learners acquire vocabulary from content they already want to watch without turning watching into homework?
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* 02 — WHAT I OWNED */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>02 — What I Owned</span>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <p className="text-editorial-body text-slate-200/70 mb-8 max-w-3xl">
              This was a 4-person team. I personally:
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ScrollReveal delay={0.1}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Designed the primary web-app flows</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Designed the logo and visual identity</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Designed and built Community Groups for vocabulary sharing</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Implemented FSRS spaced-repetition scheduling</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Added Netflix compatibility to the Chrome extension</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Integrated progress analytics</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="border-b border-cyan-900/10 pb-4">
                <p className="text-editorial-body text-slate-200">Helped oversee the overall product development</p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* 03 — FROM DEADBIRD TO CLIPIT */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>03 — From Deadbird to ClipIt</span>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-12 mb-12">
            <ScrollReveal className="col-span-12 lg:col-span-7">
              <h2 className="text-editorial-statement text-white font-semibold mb-6 leading-tight">
                The first idea was much bigger than the product we shipped.
              </h2>

              <div className="space-y-6 text-editorial-body text-slate-200/70">
                <p>
                  The early product, originally called Deadbird, included a TikTok-style feed, webpage word replacement, vocabulary capture from reading, and broader language-learning features.
                </p>

                <p>
                  We narrowed the product to video, focused on Korean and Ukrainian, and moved everything else to the roadmap.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* Before and After comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
            <ScrollReveal delay={0.2} className="flex">
              <div className="relative w-full">
                <img
                  src="/images/cibefore.png"
                  alt="Early Deadbird prototype with dark theme"
                  className="w-full h-full object-cover rounded border border-cyan-900/20"
                  style={{ maxHeight: '450px' }}
                />
                <div className="absolute top-4 right-4 text-editorial-meta text-cyan-400/70 italic bg-slate-900/80 px-4 py-2 rounded">
                  before
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} className="flex">
              <div className="relative w-full">
                <img
                  src="/images/ciafter.png"
                  alt="Final ClipIt interface with light theme"
                  className="w-full h-full object-cover rounded border border-cyan-900/20"
                  style={{ maxHeight: '450px' }}
                />
                <div className="absolute top-4 right-4 text-editorial-meta text-cyan-300/70 italic bg-slate-900/80 px-4 py-2 rounded">
                  after
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.4} className="mt-8">
            <div className="bg-cyan-500/5 border-l-4 border-cyan-500/30 pl-8 py-6">
              <p className="text-editorial-body text-slate-200 italic">
                Narrowing the scope made the product stronger.
              </p>
            </div>
          </ScrollReveal>
        </section>

        {/* 04 — THE HARDEST PRODUCT DECISION */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>04 — The Hardest Product Decision</span>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-12">
            <ScrollReveal className="col-span-12 lg:col-span-7">
              <h2 className="text-editorial-statement text-white font-semibold mb-6 leading-tight">
                More vocabulary wasn't always better.
              </h2>

              <div className="space-y-6 text-editorial-body text-slate-200/70">
                <p>
                  Mining every possible word created an overwhelming review queue and could interfere with the audio/context experience.
                </p>

                <p>
                  We changed the system so vocabulary quantity scales with video length—roughly 3–5 cards are surfaced per 10 minutes. High-frequency vocabulary is prioritized, and rewatching a video can surface different vocabulary instead of repeating the same words.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="col-span-12 lg:col-span-5 lg:pt-12">
              <div className="space-y-4">
                <div className="border-b border-cyan-900/10 pb-4">
                  <p className="text-editorial-body text-cyan-300">Vocabulary quantity scales with video length</p>
                </div>
                <div className="border-b border-cyan-900/10 pb-4">
                  <p className="text-editorial-body text-cyan-300">~3–5 cards per 10 minutes</p>
                </div>
                <div className="border-b border-cyan-900/10 pb-4">
                  <p className="text-editorial-body text-cyan-300">High-frequency vocabulary prioritized</p>
                </div>
                <div className="border-b border-cyan-900/10 pb-4">
                  <p className="text-editorial-body text-cyan-300">Rewatching surfaces new words</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* 05 — THE HARDEST THING I SOLVED */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>05 — The Hardest Thing I Solved</span>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-12 mb-8">
            <ScrollReveal className="col-span-12 lg:col-span-7">
              <h2 className="text-editorial-statement text-white font-semibold mb-6 leading-tight">
                The best capture flow was the one learners barely noticed.
              </h2>

              <div className="space-y-6 text-editorial-body text-slate-200/70">
                <p>
                  The point of ClipIt breaks if learners must stop the video, switch apps, and manually make cards. I worked on reducing vocabulary capture to one action while preserving the exact sentence and audio context.
                </p>

                <p>
                  Netflix was especially difficult because of how its subtitles were structured, but I was able to get the integration working.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="col-span-12 lg:col-span-5 lg:pt-12">
              <div className="bg-cyan-500/5 border-l-4 border-cyan-500/30 pl-6 py-4">
                <p className="text-editorial-body text-slate-200 italic">
                  The watching was supposed to be the lesson.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* YouTube/video vocabulary interface */}
          <ScrollReveal delay={0.3}>
            <div className="relative rounded overflow-hidden border border-cyan-900/20">
              <img
                src="/images/clipitytv.png"
                alt="ClipIt vocabulary capture interface on YouTube"
                className="w-full h-auto"
              />
            </div>
          </ScrollReveal>
        </section>

        {/* 06 — THE FINAL EXPERIENCE */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>06 — The Final Experience</span>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <h2 className="text-editorial-statement text-white font-semibold mb-12 leading-tight">
              Watch → Capture → Review
            </h2>
          </ScrollReveal>

          {/* Demo Video */}
          <ScrollReveal delay={0.1} className="mb-12" id="demo">
            <div className="space-y-4">
              <h3 className="text-editorial-title text-cyan-300 font-semibold">
                Watch the demo
              </h3>
              <a
                href="https://drive.google.com/file/d/1229t8D9MofTdx-JMJfdBRQCSDTnh7BEB/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="block relative rounded overflow-hidden border border-cyan-900/20 bg-slate-900/30 hover:border-cyan-500/40 transition-all duration-300 group"
              >
                <div className="relative">
                  <img
                    src="/images/ci2.portfolio.png"
                    alt="ClipIt Demo Preview"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/40 to-transparent flex items-center justify-center">
                    <div className="flex items-center gap-3 px-8 py-4 bg-cyan-500 text-slate-900 rounded-lg font-medium group-hover:bg-cyan-400 transition-all duration-300 group-hover:scale-105 shadow-lg shadow-cyan-500/20">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                      <span>Watch Demo</span>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </ScrollReveal>

          {/* Daily Review screenshot - supporting evidence */}
          <ScrollReveal delay={0.2}>
            <div className="flex justify-center">
              <div className="relative rounded overflow-hidden border border-cyan-900/20 w-[85%]">
                <img
                  src="/images/cidr.png"
                  alt="ClipIt daily review interface showing contextual vocabulary practice"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </ScrollReveal>
        </section>

        {/* 07 — VALIDATION */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>07 — Validation</span>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <h2 className="text-editorial-statement text-white font-semibold mb-8 leading-tight">
              It stopped feeling like a class project when real students started using it.
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <p className="text-editorial-body text-slate-200/70 max-w-3xl mb-10">
              The Head of Dartmouth's Korean language program gave feedback during development and ClipIt was piloted with students in Korean courses.
            </p>
          </ScrollReveal>

          {/* Stats grid - more prominent */}
          <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 rounded-lg p-10 mb-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <ScrollReveal delay={0.2}>
                <div className="text-center lg:text-left">
                  <div className="text-7xl lg:text-8xl font-bold text-cyan-300 mb-3">~20</div>
                  <div className="text-xl text-slate-200/80 font-medium">Students</div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.25}>
                <div className="text-center lg:text-left">
                  <div className="text-7xl lg:text-8xl font-bold text-cyan-300 mb-3">2</div>
                  <div className="text-xl text-slate-200/80 font-medium">Languages</div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.3}>
                <div className="text-center lg:text-left">
                  <div className="text-4xl lg:text-5xl font-bold text-cyan-300 mb-3">YouTube + Netflix</div>
                  <div className="text-xl text-slate-200/80 font-medium">Platforms</div>
                </div>
              </ScrollReveal>
            </div>
          </div>

          <ScrollReveal delay={0.4}>
            <p className="text-editorial-body text-slate-200/70 max-w-3xl mb-10">
              Classmates told me the product solved problems they had not realized they would encounter.
            </p>
          </ScrollReveal>

          {/* What we did not validate */}
          <ScrollReveal delay={0.5}>
            <div className="bg-slate-900/30 border border-cyan-900/20 rounded p-8">
              <div className="text-editorial-body text-cyan-300 font-medium mb-4">
                What we did not validate:
              </div>
              <div className="space-y-3 text-editorial-body text-slate-200/70">
                <p>• No strong long-term retention study across a large group</p>
                <p>• Conjugation support remains unresolved because morphology differs across languages</p>
              </div>
            </div>
          </ScrollReveal>
        </section>

        {/* 08 — WHAT I WOULD DO DIFFERENTLY */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>08 — What I Would Do Differently</span>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <h2 className="text-editorial-statement text-white font-semibold mb-8 leading-tight">
              I would design for expansion earlier.
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-12">
            <ScrollReveal delay={0.1} className="col-span-12 lg:col-span-8">
              <div className="space-y-6 text-editorial-body text-slate-200/70">
                <p>
                  We tailored the system closely to Korean and Ukrainian. If I continued the project, I would build a more modular language architecture so additional target languages could be added without redesigning the experience for each one.
                </p>

                <p>
                  I would also define the full end-to-end flow earlier instead of focusing on individual features first.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* 09 — REFLECTION */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <ScrollReveal>
            <div className="flex items-center gap-3 text-cyan-400/60 text-editorial-meta uppercase tracking-wider mb-8">
              <div className="w-1 h-1 rounded-full bg-cyan-400/60" />
              <span>09 — What I Took Away</span>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <h2 className="text-editorial-statement text-white font-semibold mb-8 leading-tight">
              Shipping taught me more than building in isolation.
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-12 gap-12">
            <ScrollReveal delay={0.1} className="col-span-12 lg:col-span-8">
              <div className="space-y-6 text-editorial-body text-slate-200/70 leading-relaxed">
                <p>
                  Real users surfaced edge cases we could not predict.
                </p>

                <p>
                  The hardest part was not the technology itself, but balancing effectiveness with what learners would realistically keep doing.
                </p>

                <p>
                  The project shifted how I thought about my role: from asking what we could implement to asking what experience would make learning easier.
                </p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.2} className="mt-8">
            <div className="bg-cyan-500/5 border-l-4 border-cyan-500/30 pl-8 py-6">
              <p className="text-editorial-body text-slate-200 italic">
                The question became less "What else can we build?" and more "Will someone actually want to keep using this?"
              </p>
            </div>
          </ScrollReveal>
        </section>

        {/* Navigation */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 border-t border-cyan-900/10">
          <div className="flex justify-center">
            <Link
              to="/#projects"
              className="inline-flex items-center gap-2 px-8 py-3 bg-cyan-500 text-slate-900 rounded-lg hover:bg-cyan-400 transition-all duration-300 font-medium hover:scale-105"
            >
              <ArrowLeft className="w-5 h-5" />
              Selected Work
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-slate-950/50 border-t border-cyan-900/30 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-cyan-200/60">
          <p>&copy; 2025 Julian Matos</p>
        </div>
      </footer>
    </div>
  );
}
